import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import EmailInviteInput from "./EmailInviteInput";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

interface Admin {
  id: string;
  name: string | null;
  email: string;
  created_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  expires_at: string;
  created_at: string;
}

type SortColumn = "email" | "joined";
type SortDirection = "asc" | "desc";

const AdminManagement = () => {
  const { agency } = useAgencyAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitation[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [showConfirmId, setShowConfirmId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("email");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const fetchAdmins = async () => {
    try {
      const { data } = await axios.get("/api/agency/admins");
      setAdmins(data.admins);
      setPendingInvitations(data.pendingInvitations);
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Failed to load admins");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof Element &&
        !event.target.closest(".dropdown")
      ) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    return moment.utc(dateString).local().fromNow();
  };

  const filteredAdmins = useMemo(() => {
    const filtered = !searchQuery.trim()
      ? admins
      : admins.filter((a) =>
          a.email.toLowerCase().includes(searchQuery.toLowerCase()),
        );

    return [...filtered].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "email":
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case "joined":
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [admins, searchQuery, sortColumn, sortDirection]);

  const filteredInvitations = useMemo(() => {
    if (!searchQuery.trim()) return pendingInvitations;
    return pendingInvitations.filter((inv) =>
      inv.email.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [pendingInvitations, searchQuery]);

  const handleInviteSuccess = () => {
    toast.success("Invitations sent successfully");
    fetchAdmins();
  };

  const toggleDropdown = (id: string) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleRemoveAdmin = async (adminId: string) => {
    setActionLoading((prev) => ({ ...prev, [adminId]: true }));
    try {
      await axios.delete(`/api/agency/admins/${adminId}`);
      toast.success("Admin removed successfully");
      await fetchAdmins();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to remove admin");
    } finally {
      setShowConfirmId(null);
      setActionLoading((prev) => ({ ...prev, [adminId]: false }));
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    setActionLoading((prev) => ({ ...prev, [invitationId]: true }));
    try {
      await axios.post("/api/agency/resend-admin-invite", { invitationId });
      const inv = pendingInvitations.find((i) => i.id === invitationId);
      toast.success(`Invitation resent to ${inv?.email}`);
      setOpenDropdownId(null);
      await fetchAdmins();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to resend invitation");
    } finally {
      setActionLoading((prev) => ({ ...prev, [invitationId]: false }));
    }
  };

  const handleCancelInvite = async (invitationId: string) => {
    setActionLoading((prev) => ({ ...prev, [invitationId]: true }));
    try {
      await axios.delete(`/api/agency/admin-invitations/${invitationId}`);
      toast.success("Invitation cancelled");
      await fetchAdmins();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to cancel invitation");
    } finally {
      setShowConfirmId(null);
      setActionLoading((prev) => ({ ...prev, [invitationId]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  const SortIcon = ({ column }: { column: SortColumn }) =>
    sortColumn === column ? (
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        {sortDirection === "asc" ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
      </div>
    ) : null;

  return (
    <>
      {/* Invite Admin */}
      <div className="card border border-base-300 bg-base-100">
        <div className="card-body">
          <h2 className="card-title">Invite Admins</h2>
          <p className="text-gray-600 dark:text-base-content/70 mb-4">
            Enter email addresses of people to invite as agency admins. They
            will be able to send invitations and edit Agency preferences. Press
            Enter, Tab, Space or add a comma after each email.
          </p>
          <EmailInviteInput
            onInvite={handleInviteSuccess}
            placeholder="Add emails..."
            endpoint="/api/agency/invite-admin"
          />
        </div>
      </div>

      {/* Admins & Invitations Table */}
      <div className="mt-8 card card-body border border-base-300 bg-base-100">
        {/* Search Bar */}
        <div className="bg-base-200 -mx-8 -mt-8 mb-6 rounded-t-2xl">
          <div className="relative px-4 py-4">
            <MagnifyingGlassIcon className="absolute left-9 top-1/2 -translate-y-1/2 h-5 w-5 text-base-content/40" />
            <Input
              type="text"
              placeholder="Search by email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-12 w-full !max-w-none border-0 shadow-none"
            />
          </div>
        </div>

        {/* Admins Section */}
        <div className="mb-8">
          <div className="overflow-visible">
            <h2 className="card-title">Admins ({filteredAdmins.length})</h2>
            <table className="w-full border-collapse mt-2">
              <thead className="bg-base-200">
                <tr>
                  <th
                    className="p-2 w-1/3 text-left text-sm font-semibold text-base-content/60 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
                    onClick={() => handleSort("email")}
                  >
                    <div className="pr-6">Email</div>
                    <SortIcon column="email" />
                  </th>
                  <th
                    className="p-2 w-1/3 text-left text-sm font-semibold text-base-content/60 cursor-pointer hover:bg-gray-100 dark:hover:bg-base-300 relative"
                    onClick={() => handleSort("joined")}
                  >
                    <div className="pr-6">Joined</div>
                    <SortIcon column="joined" />
                  </th>
                  <th className="p-2 w-1/3 text-left text-sm font-semibold text-base-content/60">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300">
                {filteredAdmins.length > 0 ? (
                  filteredAdmins.map((admin) => {
                    const isCurrentUser = admin.id === agency?.admin_id;
                    return (
                      <tr key={admin.id} className="hover:bg-base-200">
                        <td className="p-2 whitespace-nowrap text-base-content/60 font-semibold text-sm">
                          {admin.email}
                          {isCurrentUser && (
                            <span className="ml-2 badge badge-sm badge-primary">
                              You
                            </span>
                          )}
                        </td>
                        <td className="p-2 whitespace-nowrap text-base-content/60 text-sm">
                          {formatDate(admin.created_at)}
                        </td>
                        <td className="p-2 whitespace-nowrap relative">
                          {isCurrentUser ? (
                            <Button size="xs" variant="success" disabled>
                              Active
                            </Button>
                          ) : showConfirmId === admin.id ? (
                            <div className="flex space-x-2">
                              <Button
                                variant="danger"
                                size="xs"
                                className="text-xs py-1"
                                onClick={() => handleRemoveAdmin(admin.id)}
                                disabled={actionLoading[admin.id]}
                              >
                                {actionLoading[admin.id]
                                  ? "Removing..."
                                  : "Confirm"}
                              </Button>
                              <Button
                                variant="secondary"
                                size="xs"
                                className="text-xs py-1"
                                onClick={() => setShowConfirmId(null)}
                                disabled={actionLoading[admin.id]}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="dropdown dropdown-right">
                              <Button
                                size="xs"
                                variant="success"
                                onClick={() => toggleDropdown(admin.id)}
                              >
                                <span>Active</span>
                                <ChevronDownIcon className="h-3 w-3" />
                              </Button>
                              {openDropdownId === admin.id && (
                                <ul
                                  tabIndex={0}
                                  className="dropdown-content z-[100] menu p-1 shadow bg-base-100 rounded-lg w-40 border border-base-300 absolute right-0 top-full mt-1"
                                >
                                  <li>
                                    <button
                                      type="button"
                                      className="text-xs text-red-600 dark:text-red-400"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        setShowConfirmId(admin.id);
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </li>
                                </ul>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={3} className="p-4 text-center text-base-content/60">
                      No admins found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending Invites Section */}
        <div>
          <div className="overflow-visible">
            <h2 className="card-title">
              Pending Invites ({filteredInvitations.length})
            </h2>
            <table className="w-full border-collapse mt-2">
              <thead className="bg-base-200">
                <tr>
                  <th className="p-2 w-1/3 text-left text-sm font-semibold text-base-content/60">
                    Email
                  </th>
                  <th className="p-2 w-1/3 text-left text-sm font-semibold text-base-content/60">
                    Invited At
                  </th>
                  <th className="p-2 w-1/3 text-left text-sm font-semibold text-base-content/60">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300">
                {filteredInvitations.length > 0 ? (
                  filteredInvitations.map((inv) => (
                    <tr key={inv.id} className="hover:bg-base-200">
                      <td className="p-2 whitespace-nowrap text-sm text-base-content/60 font-semibold">
                        {inv.email}
                      </td>
                      <td className="p-2 whitespace-nowrap text-sm text-base-content/60">
                        {formatDate(inv.created_at)}
                      </td>
                      <td className="p-2 text-sm whitespace-nowrap relative">
                        {showConfirmId === inv.id ? (
                          <div className="flex space-x-2">
                            <Button
                              variant="danger"
                              size="xs"
                              className="text-xs py-1"
                              onClick={() => handleCancelInvite(inv.id)}
                              disabled={actionLoading[inv.id]}
                            >
                              {actionLoading[inv.id]
                                ? "Removing..."
                                : "Confirm"}
                            </Button>
                            <Button
                              variant="secondary"
                              size="xs"
                              className="text-xs py-1"
                              onClick={() => setShowConfirmId(null)}
                              disabled={actionLoading[inv.id]}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="dropdown dropdown-right">
                            {new Date(inv.expires_at) < new Date() ? (
                              <Button
                                size="xs"
                                variant="danger"
                                onClick={() => toggleDropdown(inv.id)}
                              >
                                <span>Expired</span>
                                <ChevronDownIcon className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button
                                size="xs"
                                variant="warning"
                                onClick={() => toggleDropdown(inv.id)}
                              >
                                <span>Pending</span>
                                <ChevronDownIcon className="h-3 w-3" />
                              </Button>
                            )}
                            {openDropdownId === inv.id && (
                              <ul
                                tabIndex={0}
                                className="dropdown-content z-[100] menu p-1 shadow bg-base-100 rounded-lg w-40 border border-base-300 absolute right-0 top-full mt-1"
                              >
                                <li>
                                  <button
                                    type="button"
                                    className="text-xs"
                                    onClick={() => handleResendInvite(inv.id)}
                                  >
                                    Resend
                                  </button>
                                </li>
                                <li className="border-t border-base-200 dark:border-base-300">
                                  <button
                                    type="button"
                                    className="text-xs text-red-600 dark:text-red-400"
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      setShowConfirmId(inv.id);
                                    }}
                                  >
                                    Remove
                                  </button>
                                </li>
                              </ul>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="p-4 text-center text-base-content/60">
                      No pending invites
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminManagement;
